<script lang="ts">
    import { onDestroy } from 'svelte';
    import { get } from 'svelte/store';
    import { push } from 'svelte-spa-router';
    import { currentUser, authReady } from '$lib/stores/auth';
    import {
        adminListUsers,
        adminDisableUser,
        adminEnableUser,
        adminPromoteUser,
        adminDemoteUser,
        adminResetUserPassword,
        humanizeError,
        type AdminUser,
        type AdminResetPasswordResult,
    } from '$lib/api';
    import AdminNav from '../components/AdminNav.svelte';
    import CopyableUrl from '../components/CopyableUrl.svelte';
    import DeleteUserModal from '../components/DeleteUserModal.svelte';

    let users: AdminUser[] = [];
    let loading = true;
    let error: string | null = null;
    let busyId: string | null = null;
    let resetUrl: AdminResetPasswordResult | null = null;
    let resetForUsername: string | null = null;
    let resetExpiresIn = '24h';
    const RESET_EXPIRY_LABELS: Record<string, string> = {
        '30m': '30 minutes',
        '24h': '24 hours',
        '7d': '7 days',
        '30d': '30 days',
    };
    let deleteTarget: AdminUser | null = null;

    async function load() {
        if (!get(authReady)) return;
        if (!$currentUser || $currentUser.role !== 'admin') {
            push('/map');
            return;
        }
        loading = true;
        error = null;
        try {
            users = await adminListUsers();
        } catch (e: any) {
            error = humanizeError(e, 'Failed to load users');
        } finally {
            loading = false;
        }
    }

    function fmtTime(s: string | null): string {
        if (!s) return '—';
        return new Date(s).toLocaleString([], {
            month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });
    }

    async function withBusy<T>(id: string, fn: () => Promise<T>): Promise<T | null> {
        if (busyId) return null;
        busyId = id;
        error = null;
        try {
            return await fn();
        } catch (e: any) {
            error = humanizeError(e, 'Action failed');
            return null;
        } finally {
            busyId = null;
        }
    }

    async function onDisable(u: AdminUser) {
        if (!window.confirm(`Disable ${u.username}? They will be immediately logged out and unable to log in again until re-enabled.`)) return;
        const result = await withBusy(u.id, () => adminDisableUser(u.id));
        if (result) await load();
    }

    async function onEnable(u: AdminUser) {
        const result = await withBusy(u.id, () => adminEnableUser(u.id));
        if (result) await load();
    }

    async function onPromote(u: AdminUser) {
        if (!window.confirm(`Promote ${u.username} to admin? Admins can manage users and trigger syncs.`)) return;
        const result = await withBusy(u.id, () => adminPromoteUser(u.id));
        if (result) await load();
    }

    async function onDemote(u: AdminUser) {
        if (!window.confirm(`Demote ${u.username} from admin to volunteer?`)) return;
        const result = await withBusy(u.id, () => adminDemoteUser(u.id));
        if (result) await load();
    }

    async function onResetPassword(u: AdminUser) {
        const expiryLabel = RESET_EXPIRY_LABELS[resetExpiresIn] ?? resetExpiresIn;
        if (!window.confirm(`Generate a password-reset link for ${u.username}? It will expire in ${expiryLabel}. Their existing password remains valid until the link is used.`)) return;
        const result = await withBusy(u.id, () => adminResetUserPassword(u.id, resetExpiresIn));
        if (result) {
            resetUrl = result;
            resetForUsername = u.username;
        }
    }

    function dismissResetUrl() {
        resetUrl = null;
        resetForUsername = null;
    }

    function onDelete(u: AdminUser) {
        // First barrier: the click. Second barrier lives in the modal, where the
        // admin must type the exact username before the Delete button enables.
        deleteTarget = u;
    }

    async function onDeleteDone() {
        deleteTarget = null;
        await load();
    }

    const unsubUser = currentUser.subscribe(() => { load(); });
    const unsubReady = authReady.subscribe(() => { load(); });
    onDestroy(() => { unsubUser(); unsubReady(); });
</script>

<div class="admin-page">
    <header class="admin-header">
        <span class="eyebrow">Admin · Users</span>
        <h1>People with access</h1>
        <p class="page-sub">Enable, disable, promote, or reset passwords for everyone on the campaign.</p>
    </header>

    <AdminNav active="users" />

    {#if error}
        <p class="msg msg-error">{error}</p>
    {/if}

    {#if resetUrl}
        <div class="reset-banner">
            <div class="reset-head">
                <span><span class="eyebrow">Reset link</span> &nbsp;for <strong class="mono">{resetForUsername}</strong></span>
                <button class="dismiss" on:click={dismissResetUrl} aria-label="Dismiss">×</button>
            </div>
            <p class="reset-sub">
                Single-use · expires <span class="mono">{fmtTime(resetUrl.expires_at)}</span>.
                Send this privately. The user's existing password remains valid until they use the link.
            </p>
            <CopyableUrl value={resetUrl.reset_url} label="Reset URL" />
        </div>
    {/if}

    <div class="reset-expiry-row">
        <label class="reset-expiry">
            <span>Reset links expire in</span>
            <select bind:value={resetExpiresIn}>
                {#each Object.entries(RESET_EXPIRY_LABELS) as [value, label] (value)}
                    <option {value}>{label}</option>
                {/each}
            </select>
        </label>
    </div>

    {#if loading}
        <p class="loading">Loading…</p>
    {:else if users.length === 0}
        <p class="muted">No users.</p>
    {:else}
        <div class="table-wrap">
            <table>
                <thead>
                    <tr>
                        <th>Username</th>
                        <th>Display name</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Last login</th>
                        <th>Created</th>
                        <th class="actions-th">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {#each users as u (u.id)}
                        {@const self = $currentUser && u.username === $currentUser.username}
                        <tr class:disabled={u.disabled_at !== null}>
                            <td class="mono">
                                {u.username}
                                {#if self}<span class="self-pill">you</span>{/if}
                            </td>
                            <td>{u.display_name ?? '—'}</td>
                            <td>
                                <span class="badge {u.role === 'admin' ? 'badge-admin' : 'badge-vol'}">
                                    {u.role}
                                </span>
                            </td>
                            <td>
                                {#if u.disabled_at}
                                    <span class="badge badge-error">disabled</span>
                                {:else}
                                    <span class="badge badge-ok">active</span>
                                {/if}
                            </td>
                            <td class="mono">{fmtTime(u.last_login_at)}</td>
                            <td class="mono">{fmtTime(u.created_at)}</td>
                            <td class="actions">
                                {#if u.disabled_at}
                                    <button class="row-btn" on:click={() => onEnable(u)} disabled={busyId === u.id}>Enable</button>
                                {:else if !self}
                                    <button class="row-btn" on:click={() => onDisable(u)} disabled={busyId === u.id}>Disable</button>
                                {/if}
                                {#if u.role === 'volunteer'}
                                    <button class="row-btn" on:click={() => onPromote(u)} disabled={busyId === u.id}>Promote</button>
                                {:else if !self}
                                    <button class="row-btn" on:click={() => onDemote(u)} disabled={busyId === u.id}>Demote</button>
                                {/if}
                                <button class="row-btn row-btn-quiet" on:click={() => onResetPassword(u)} disabled={busyId === u.id}>Reset password</button>
                                {#if !self && u.role === 'volunteer'}
                                    <button class="row-btn row-btn-danger" on:click={() => onDelete(u)} disabled={busyId === u.id}>Delete</button>
                                {/if}
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>
    {/if}
</div>

{#if deleteTarget}
    <DeleteUserModal
        user={deleteTarget}
        on:close={() => (deleteTarget = null)}
        on:done={onDeleteDone}
    />
{/if}

<style>
    .admin-page { max-width: 1180px; margin: 0 auto; padding: var(--s-7) var(--s-5) var(--s-8); }
    .admin-header { margin-bottom: var(--s-5); }
    .admin-header .eyebrow { color: var(--leaf-bright); display: block; margin-bottom: var(--s-2); }
    .admin-header h1 {
        font-family: var(--font-display);
        font-size: clamp(2rem, 5vw, 2.8rem);
        margin: 0 0 var(--s-2); font-weight: 600;
    }
    .page-sub { color: var(--ink-soft); font-size: 1rem; margin: 0; }

    .loading { color: var(--ink-soft); padding: var(--s-5) 0; }
    .muted { color: var(--ink-muted); font-style: italic; }

    .msg { margin: 0 0 var(--s-4); padding: 10px 14px; font-size: 0.9rem; border-left: 3px solid; }
    .msg-error { background: var(--alert-soft); border-color: var(--alert); color: var(--alert); }

    /* Tables */
    .table-wrap { background: var(--field); border: 1px solid var(--rule); overflow-x: auto; }
    table { width: 100%; border-collapse: collapse; font-size: 0.9rem; min-width: 820px; }
    th, td { text-align: left; padding: 12px 14px; border-bottom: 1px solid var(--rule); vertical-align: middle; }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover { background: var(--paper-soft); }
    th {
        font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.1em;
        color: var(--ink-muted); font-weight: 600; background: var(--paper);
        border-bottom: 2px solid var(--rule); white-space: nowrap;
    }
    tr.disabled { opacity: 0.55; }
    .actions-th { text-align: right; }
    .actions {
        display: flex; gap: 6px; flex-wrap: wrap; justify-content: flex-end;
    }

    /* Row buttons */
    .row-btn {
        font-family: var(--font-body);
        padding: 5px 12px;
        font-size: 0.78rem;
        font-weight: 600;
        border: 1px solid var(--rule-strong);
        background: var(--field);
        color: var(--ink);
        cursor: pointer;
        transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
    }
    .row-btn:hover:not(:disabled) {
        background: var(--ink);
        color: var(--paper);
        border-color: var(--ink);
    }
    .row-btn-quiet {
        background: transparent;
        color: var(--ink-soft);
        border-color: var(--rule);
    }
    .row-btn-quiet:hover:not(:disabled) {
        background: var(--sun-soft);
        color: var(--ink);
        border-color: var(--sun);
    }
    .row-btn-danger {
        background: transparent;
        color: var(--alert);
        border-color: var(--alert);
    }
    .row-btn-danger:hover:not(:disabled) {
        background: var(--alert);
        color: var(--paper);
        border-color: var(--alert);
    }
    .row-btn:disabled { opacity: 0.45; cursor: not-allowed; }

    /* Badges */
    .badge {
        display: inline-block;
        padding: 3px 10px;
        font-size: 0.72rem; font-weight: 600;
        letter-spacing: 0.04em; text-transform: lowercase;
        border: 1px solid;
    }
    .badge-ok    { background: var(--leaf-soft);  color: var(--leaf-ink); border-color: var(--leaf-bright); }
    .badge-error { background: var(--alert-soft); color: var(--alert);    border-color: var(--alert); }
    .badge-admin { background: var(--sun-soft);   color: var(--ink);      border-color: var(--sun); }
    .badge-vol   { background: var(--paper);      color: var(--ink-soft); border-color: var(--rule-strong); }

    .self-pill {
        margin-left: 6px;
        font-size: 0.65rem;
        background: var(--ink);
        color: var(--paper);
        padding: 1px 6px;
        text-transform: uppercase;
        font-weight: 700;
        letter-spacing: .06em;
    }

    /* Reset-link banner */
    .reset-banner {
        border: 1px solid var(--leaf-bright);
        border-left: 4px solid var(--leaf);
        background: var(--leaf-soft);
        padding: var(--s-4);
        margin-bottom: var(--s-5);
    }
    .reset-head {
        display: flex; justify-content: space-between; align-items: center;
        margin-bottom: 4px; gap: var(--s-3);
    }
    .reset-head .eyebrow { color: var(--leaf-ink); }
    .reset-sub { margin: 0 0 var(--s-3); font-size: 0.85rem; color: var(--leaf-ink); }
    .dismiss {
        background: transparent; border: 0; font-size: 1.4rem; line-height: 1;
        cursor: pointer; color: var(--ink-soft); padding: 0 6px;
    }
    .dismiss:hover { color: var(--ink); }

    /* Reset-link expiry picker */
    .reset-expiry-row {
        display: flex; justify-content: flex-end;
        margin-bottom: var(--s-3);
    }
    .reset-expiry {
        display: inline-flex; align-items: center; gap: var(--s-2);
        font-size: 0.85rem; color: var(--ink-soft);
    }
</style>
